/**
 * AdminPropertyTypesTab.jsx — v2.88 (Sprint P0 Production Fix)
 * =============================================================
 *
 * Full CRUD UI for the `property_types` master collection.
 *
 * Mounted as a sub-tab of `/admin/filters` (see AdminFiltersTab.jsx).
 * Consumes `GET /api/admin/property-types` for the grid + used_count.
 *
 * UX contract
 * -----------
 * - Types are ordered by `display_order`; up/down arrows shift by ±5
 *   and PATCH the row.
 * - "Rename" edits `name` inline; slug is derived server-side.
 * - "Deactivate" is the primary destructive action for referenced types
 *   (soft-delete). "Delete" only shows for zero-reference rows.
 * - "Legacy" locked rows (Flat/Apartment) render as INACTIVE + read-only
 *   with a subtle badge — they're preserve-only.
 */

import React, { useEffect, useMemo, useState } from "react";
import api, { apiErrorMsg } from "../lib/api";
import { toast } from "sonner";
import {
  Layers, Plus, Trash2, ArrowUp, ArrowDown, Pencil, Check, X,
  ToggleLeft, ToggleRight, Loader2, ShieldAlert, Lock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

const CATEGORY_OPTIONS = [
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "land", label: "Land" },
];

const LISTING_TYPE_OPTIONS = ["Sale", "Rent", "Lease"];

const emptyDraft = () => ({
  name: "",
  category: "residential",
  allowed_listing_types: ["Sale", "Rent", "Lease"],
  aliases: [],
  is_active: true,
});

export default function AdminPropertyTypesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/property-types");
      setRows(r.data.property_types || []);
    } catch (err) {
      toast.error(apiErrorMsg(err, "Could not load property types"));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const activeCount = useMemo(
    () => rows.filter((r) => r.is_active).length,
    [rows],
  );
  const totalUsed = useMemo(
    () => rows.reduce((sum, r) => sum + (r.used_count || 0), 0),
    [rows],
  );

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditDraft({
      name: row.name,
      category: row.category,
      allowed_listing_types: [...(row.allowed_listing_types || [])],
      aliases: [...(row.aliases || [])],
      is_active: row.is_active,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({});
  };

  const saveEdit = async (row) => {
    setBusy(true);
    try {
      await api.patch(`/admin/property-types/${row.id}`, editDraft);
      toast.success(`${editDraft.name} saved`);
      await load();
      cancelEdit();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Save failed"));
    }
    setBusy(false);
  };

  const toggleActive = async (row) => {
    if (row.is_locked) {
      toast.error("Locked legacy types cannot be reactivated");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/property-types/${row.id}`, { is_active: !row.is_active });
      await load();
      toast.success(`${row.name} → ${!row.is_active ? "active" : "inactive"}`);
    } catch (err) {
      toast.error(apiErrorMsg(err, "Toggle failed"));
    }
    setBusy(false);
  };

  const reorderRow = async (row, dir) => {
    const sorted = [...rows].sort((a, b) => a.display_order - b.display_order);
    const idx = sorted.findIndex((r) => r.id === row.id);
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const partner = sorted[swapIdx];
    setBusy(true);
    try {
      await api.post("/admin/property-types/reorder", {
        order: [
          { id: row.id, display_order: partner.display_order },
          { id: partner.id, display_order: row.display_order },
        ],
      });
      await load();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Reorder failed"));
    }
    setBusy(false);
  };

  const deleteRow = async (row) => {
    if (row.is_locked) {
      toast.error("Locked legacy types cannot be deleted");
      return;
    }
    const msg = row.used_count > 0
      ? `'${row.name}' is used by ${row.used_count} listing(s). Deactivate it instead? (soft-delete)`
      : `Delete '${row.name}' permanently? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await api.delete(`/admin/property-types/${row.id}`);
      toast.success(r.data.mode === "soft_delete"
        ? `${row.name} deactivated (${r.data.used_count} references preserved)`
        : `${row.name} deleted`,
      );
      await load();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Delete failed"));
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="admin-property-types-tab">
      {/* Header + Add */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading font-semibold text-lg tracking-tight inline-flex items-center gap-2">
            <Layers size={16} /> Property Types Master
          </h3>
          <p className="text-[12px] text-[#71717A] mt-1">
            Single source of truth for the property type catalog. Changes propagate to the public filter, Add-Property form, and admin editor instantly.
            <span className="ml-2 text-zinc-500">
              {loading ? "Loading…" : `${activeCount} active · ${rows.length} total · ${totalUsed} listings tagged`}
            </span>
          </p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="btn-brand rounded-sm" data-testid="pt-add-btn">
          <Plus size={13} className="mr-1.5" /> Add Property Type
        </Button>
      </div>

      {/* Grid */}
      <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-[#FAFAFA] border-b border-[#E4E4E7]">
            <tr className="text-left">
              <th className="px-3 py-2.5 font-semibold text-zinc-700 w-24">Order</th>
              <th className="px-3 py-2.5 font-semibold text-zinc-700">Name</th>
              <th className="px-3 py-2.5 font-semibold text-zinc-700 w-32">Category</th>
              <th className="px-3 py-2.5 font-semibold text-zinc-700 w-40">Listing Types</th>
              <th className="px-3 py-2.5 font-semibold text-zinc-700 w-24 text-center">Usage</th>
              <th className="px-3 py-2.5 font-semibold text-zinc-700 w-28">Status</th>
              <th className="px-3 py-2.5 font-semibold text-zinc-700 w-44 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-500">
                <Loader2 className="inline animate-spin mr-2" size={14} /> Loading property types…
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-500">
                No property types configured. Click &quot;Add Property Type&quot; to seed the catalog.
              </td></tr>
            )}
            {!loading && rows.map((row, idx) => {
              const isEditing = editingId === row.id;
              const isLocked = !!row.is_locked;
              return (
                <tr
                  key={row.id}
                  className={`border-b border-[#F4F4F5] hover:bg-[#FAFAFA] ${!row.is_active ? "bg-zinc-50/60" : ""}`}
                  data-testid={`pt-row-${row.slug}`}
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => reorderRow(row, "up")}
                        disabled={busy || idx === 0 || isLocked}
                        className="p-1 rounded hover:bg-zinc-200 disabled:opacity-30"
                        data-testid={`pt-up-${row.slug}`}
                      ><ArrowUp size={12} /></button>
                      <button
                        onClick={() => reorderRow(row, "down")}
                        disabled={busy || idx === rows.length - 1 || isLocked}
                        className="p-1 rounded hover:bg-zinc-200 disabled:opacity-30"
                        data-testid={`pt-down-${row.slug}`}
                      ><ArrowDown size={12} /></button>
                      <span className="ml-1 text-[11px] text-zinc-500 tabular-nums">{row.display_order}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {isEditing ? (
                      <Input
                        value={editDraft.name}
                        onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                        className="h-8 text-[13px]"
                        data-testid={`pt-name-input-${row.slug}`}
                      />
                    ) : (
                      <div>
                        <div className="font-semibold text-zinc-800 inline-flex items-center gap-1.5">
                          {row.name}
                          {isLocked && <Lock size={11} className="text-amber-600" />}
                        </div>
                        <div className="text-[10.5px] text-zinc-500 font-mono">{row.slug}</div>
                        {row.legacy && (
                          <div className="text-[10px] text-amber-700 font-semibold mt-0.5 inline-flex items-center gap-1">
                            <ShieldAlert size={9} /> Inactive · legacy type (preserve only)
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isEditing ? (
                      <Select
                        value={editDraft.category}
                        onValueChange={(v) => setEditDraft({ ...editDraft, category: v })}
                      >
                        <SelectTrigger className="h-8 text-[12px]" data-testid={`pt-category-select-${row.slug}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORY_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-[11.5px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700 uppercase font-semibold tracking-wide">
                        {row.category}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isEditing ? (
                      <div className="flex gap-1 flex-wrap">
                        {LISTING_TYPE_OPTIONS.map((lt) => {
                          const isOn = (editDraft.allowed_listing_types || []).includes(lt);
                          return (
                            <button
                              key={lt}
                              type="button"
                              onClick={() => {
                                const next = isOn
                                  ? editDraft.allowed_listing_types.filter((x) => x !== lt)
                                  : [...editDraft.allowed_listing_types, lt];
                                setEditDraft({ ...editDraft, allowed_listing_types: next });
                              }}
                              className={`px-2 py-0.5 text-[11px] rounded-full border font-medium ${
                                isOn ? "bg-[#EEF4FF] border-[#0055FF] text-[#0044CC]" : "border-zinc-300 text-zinc-500"
                              }`}
                              data-testid={`pt-lt-${row.slug}-${lt.toLowerCase()}`}
                            >{lt}</button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex gap-1 flex-wrap">
                        {(row.allowed_listing_types || []).map((lt) => (
                          <span key={lt} className="px-1.5 py-0.5 text-[10.5px] rounded-full bg-zinc-100 text-zinc-600 font-medium">{lt}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[13px] font-semibold tabular-nums ${row.used_count > 0 ? "text-[#0055FF]" : "text-zinc-400"}`}>
                      {row.used_count || 0}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => toggleActive(row)}
                      disabled={busy || isLocked}
                      className={`inline-flex items-center gap-1 text-[11.5px] font-semibold ${
                        row.is_active ? "text-emerald-700" : "text-zinc-400"
                      } disabled:opacity-40`}
                      data-testid={`pt-toggle-${row.slug}`}
                    >
                      {row.is_active
                        ? (<><ToggleRight size={14} /> Active</>)
                        : (<><ToggleLeft size={14} /> Inactive</>)}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isEditing ? (
                      <div className="inline-flex gap-1">
                        <Button size="sm" onClick={() => saveEdit(row)} disabled={busy} className="btn-brand h-7 px-2.5 rounded-sm" data-testid={`pt-save-${row.slug}`}>
                          <Check size={12} className="mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit} className="h-7 px-2.5 rounded-sm" data-testid={`pt-cancel-${row.slug}`}>
                          <X size={12} />
                        </Button>
                      </div>
                    ) : (
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => startEdit(row)} disabled={busy || isLocked} className="h-7 px-2.5 rounded-sm" data-testid={`pt-edit-${row.slug}`}>
                          <Pencil size={12} className="mr-1" /> Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteRow(row)}
                          disabled={busy || isLocked}
                          className="h-7 px-2.5 rounded-sm text-red-600 hover:bg-red-50 border-red-200"
                          data-testid={`pt-delete-${row.slug}`}
                        >
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AddPropertyTypeDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSaved={async () => { setShowAdd(false); await load(); }}
      />
    </div>
  );
}

function AddPropertyTypeDialog({ open, onClose, onSaved }) {
  const [draft, setDraft] = useState(emptyDraft());
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setDraft(emptyDraft()); }, [open]);

  const submit = async () => {
    if (!draft.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (draft.allowed_listing_types.length === 0) {
      toast.error("At least one listing type is required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/property-types", draft);
      toast.success(`${draft.name} added`);
      await onSaved();
    } catch (err) {
      toast.error(apiErrorMsg(err, "Create failed"));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="pt-add-dialog">
        <DialogHeader>
          <DialogTitle>Add Property Type</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[12px] font-semibold text-zinc-700 mb-1 block">Name *</label>
            <Input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Farm House, Warehouse, Studio Apartment"
              data-testid="pt-add-name"
            />
            <p className="text-[10.5px] text-zinc-500 mt-1">
              Slug auto-derived (URL-safe). Existing listings referring to this name become editable immediately.
            </p>
          </div>
          <div>
            <label className="text-[12px] font-semibold text-zinc-700 mb-1 block">Category *</label>
            <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
              <SelectTrigger data-testid="pt-add-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[12px] font-semibold text-zinc-700 mb-1 block">Allowed Listing Types *</label>
            <div className="flex gap-1.5">
              {LISTING_TYPE_OPTIONS.map((lt) => {
                const isOn = draft.allowed_listing_types.includes(lt);
                return (
                  <button
                    key={lt}
                    type="button"
                    onClick={() => {
                      const next = isOn
                        ? draft.allowed_listing_types.filter((x) => x !== lt)
                        : [...draft.allowed_listing_types, lt];
                      setDraft({ ...draft, allowed_listing_types: next });
                    }}
                    className={`px-3 py-1 text-[12px] rounded-full border font-semibold transition-colors ${
                      isOn ? "bg-[#EEF4FF] border-[#0055FF] text-[#0044CC]" : "border-zinc-300 text-zinc-500 hover:border-zinc-500"
                    }`}
                    data-testid={`pt-add-lt-${lt.toLowerCase()}`}
                  >{lt}</button>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="pt-add-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="btn-brand rounded-sm" data-testid="pt-add-submit">
            {busy ? "Adding…" : "Add Type"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
